<template>
  <div
    v-if="blok"
    ref="parentContainerRef"
    v-editable="blok"
    class="relative w-full"
    :class="[
      marginClasses,
      {
        'aspect-[32/9]': aspectRatioLabel === '32/9',
        'aspect-[21/9]': aspectRatioLabel === '21/9',
        'aspect-[18/9]': aspectRatioLabel === '18/9',
        'aspect-[16/9]': aspectRatioLabel === '16/9',
        'aspect-[16/10]': aspectRatioLabel === '16/10',
        'aspect-[3/2]': aspectRatioLabel === '3/2',
        'aspect-[4/3]': aspectRatioLabel === '4/3',
        'aspect-[1/1]': aspectRatioLabel === '1/1',
        'aspect-[9/16]': aspectRatioLabel === '9/16',
        'aspect-[7/10]': aspectRatioLabel === '7/10',
        'aspect-[2/1]': aspectRatioLabel === '2/1',
        'aspect-[3/1]': aspectRatioLabel === '3/1',
      },
    ]"
  >
    <!-- Thumbnail overlay -->
    <div
      v-if="thumbnailUrl"
      class="absolute inset-0 z-10 transition-opacity duration-500"
      :class="{ 'opacity-0': isVideoPlaying, 'opacity-100': !isVideoPlaying }"
    >
      <img
        :src="thumbnailUrl"
        :alt="blok.title || 'Video thumbnail'"
        class="size-full object-contain"
        @error="onThumbnailError"
      />
    </div>

    <!-- Video container -->
    <div
      ref="videoContainerRef"
      class="size-full transition-opacity duration-300"
      :class="{ 'opacity-0': !isVideoPlaying, 'opacity-100': isVideoPlaying }"
    />
    <p class="sr-only">Transcript: {{ transcript }}</p>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, computed } from 'vue'
import Player, { type VimeoEmbedParameters, type VimeoUrl } from '@vimeo/player'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import type { SbVimeoVideo } from '~/cms/storyblok/types/storyblok.gen'

interface VimeoVideoProps {
  blok: SbVimeoVideo
  autoplay?: boolean
  loop?: boolean
  muted?: boolean
  controls?: boolean
  background?: boolean
  altDescription?: string
}

const props = withDefaults(defineProps<VimeoVideoProps>(), {
  background: true,
  altDescription: '',
})

const { marginClasses } = useStoryblokMargins(props.blok)
const videoContainerRef = ref<HTMLElement | null>(null)
const isVideoPlaying = ref(false)
const parentContainerRef = ref<HTMLElement | null>(null)

let player: Player | null = null

const { url, thumbnailUrl, aspectRatioLabel, loaded } = useVimeoVideo(
  props.blok.link || '',
  props.blok.mobileLink,
)

const videoDimensions = computed(() => {
  const rect = parentContainerRef.value?.getBoundingClientRect()
  if (!rect) {
    return { width: 0, height: 0 }
  }
  return {
    width: rect.width,
    height: rect.height,
  }
})

const playerOptions = computed<VimeoEmbedParameters | undefined>(() => {
  if (!Player.isVimeoUrl(url.value)) {
    return undefined
  }

  return {
    url: url.value as VimeoUrl,
    background: props.background,
    autoplay: props.background ? undefined : props.autoplay,
    loop: props.background ? undefined : props.loop,
    muted: props.background ? undefined : props.muted,
    controls: props.background ? undefined : props.controls,
    playsinline: true,
    width: videoDimensions.value.width,
    height: videoDimensions.value.height,
  }
})

const transcript = computed(() => {
  return props.altDescription || props.blok.altDescription || props.blok.title
})

const onThumbnailError = () => {
  console.warn('Failed to load video thumbnail')
}

const initializePlayer = async () => {
  const options = playerOptions.value
  if (!videoContainerRef.value || !options) {
    return
  }
  try {
    player = new Player(videoContainerRef.value, options)
    await player.ready()

    player.on('play', () => {
      isVideoPlaying.value = true
    })
  } catch (error) {
    console.error('Error initializing Vimeo player:', error)
    isVideoPlaying.value = true
  }
}

const destroyPlayer = () => {
  if (player) {
    player.destroy()
    player = null
  }
}

onBeforeUnmount(() => {
  destroyPlayer()
})

watch([loaded, url], async () => {
  // Handle the case where the url changes because of mobile/desktop switch
  destroyPlayer()

  // We have to wait for the thumbnail aspect ratio to be non-null
  // to calculate the correct video dimensions. That's why we wait for loaded
  if (loaded.value) {
    await initializePlayer()
  }
})
</script>

<style scoped>
:deep(iframe) {
  max-width: 100%;
  max-height: 100%;
}
</style>
